import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import { authService, UniqCampLogo, isValidMobileDigits, normalizeMobileForOtp, SCHOOL_PORTAL_SESSION_KEYS } from '@uniqcamp/shared';

const Login = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { message?: string };
      sessionStorage.removeItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
      if (parsed?.message) setError(parsed.message);
    } catch {
      sessionStorage.removeItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
    }
  }, []);

  const handleSendOTP = async () => {
    setError('');
    if (!isValidMobileDigits(mobile)) {
      setError('Enter a valid mobile number (10–15 digits).');
      return;
    }
    setLoading(true);
    try {
      const result = await authService.sendOTP(normalizeMobileForOtp(mobile));
      if (result.success) {
        setActiveStep(1);
      } else {
        setError(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await authService.verifyOTP(normalizeMobileForOtp(mobile), otp);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Invalid OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left cover */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          background: 'linear-gradient(135deg, #4f227d 0%, #371755 50%, #2d1147 100%)',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Box sx={{ color: 'white', textAlign: 'center', maxWidth: 380 }}>
          <LocalTaxiIcon sx={{ fontSize: 64, mb: 2, opacity: 0.95 }} />
          <Box component="h1" sx={{ m: 0, display: 'flex', justifyContent: 'center', mb: 1 }}>
            <UniqCampLogo style={{ maxWidth: 280, maxHeight: 80, width: 'auto' }} />
          </Box>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.9, fontWeight: 500 }}>
            Cab Driver
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.8 }}>
            Sign in to manage pickups
          </Typography>
        </Box>
      </Box>

      {/* Right form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your mobile number to receive OTP
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            <Step><StepLabel>Enter Mobile</StepLabel></Step>
            <Step><StepLabel>Verify OTP</StepLabel></Step>
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {activeStep === 0 ? (
            <>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: 20 }}
                helperText="10–15 digits"
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSendOTP}
                disabled={loading || !isValidMobileDigits(mobile)}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: 6 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={loading || !otp}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
              <Button fullWidth variant="text" onClick={() => setActiveStep(0)} sx={{ mt: 1 }}>
                Change Mobile Number
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
